import { z } from "zod";
import { quantidadeSchema } from "./material";
import { dataSchema } from "../financeiro/competencia";
import { FORMAS_DE_PAGAMENTO } from "../financeiro/lancamento";
import { notaDoLancamentoSchema } from "../financeiro/nota";

/**
 * Estoque do laboratório.
 *
 * O saldo não é um campo: é a soma dos lançamentos. Por isso não existe "editar saldo" — existe
 * informar quanto se contou (e o sistema lança a diferença) ou ajustar dizendo o porquê.
 */

/** Quantidade que pode ser negativa: ajuste tira, devolução põe. Zero não significa nada. */
export const quantidadeAssinadaSchema = z.preprocess(
  (v) => (typeof v === "string" ? Number(v.replace(",", ".")) : v),
  z
    .number()
    .min(-1_000_000)
    .max(1_000_000)
    .refine((n) => n !== 0, "Informe uma quantidade diferente de zero"),
);

/** Quantidade contada, que pode ser zero ("não tenho nenhum"). */
export const quantidadeContadaSchema = z.preprocess(
  (v) => (typeof v === "string" ? Number(v.replace(",", ".")) : v),
  z.number().min(0).max(1_000_000),
);

export const sugestaoDeCompraSchema = z.object({
  /** Janela de consumo que alimenta a previsão. */
  dias: z.union([z.literal(30), z.literal(60), z.literal(90)]).default(30),
});

export const saldosSchema = z.object({
  busca: z.string().trim().max(160).optional(),
  apenasNegativos: z.boolean().optional(),
});

export const extratoSchema = z.object({
  materialLineId: z.string().min(1),
  take: z.number().int().min(1).max(200).default(50),
  skip: z.number().int().min(0).default(0),
});

export const informarSaldoSchema = z.object({
  materialLineId: z.string().min(1),
  quantidade: quantidadeContadaSchema,
  notes: z.string().trim().max(500).optional(),
});

/** Ajuste exige motivo: ajuste sem explicação é buraco no estoque com aparência de número. */
export const ajustarSchema = z.object({
  materialLineId: z.string().min(1),
  quantidade: quantidadeAssinadaSchema,
  notes: z.string().trim().min(3, "Diga o motivo do ajuste").max(500),
});

export const registrarCompraSchema = z.object({
  supplierPartnerId: z.string().min(1).nullish(),
  documento: z.string().trim().max(60).nullish(),
  /** ISO. Nem toda nota é lançada no dia em que chegou. */
  date: z.string().datetime().optional(),
  notes: z.string().trim().max(500).nullish(),
  /** Marcado por padrão: comprar é o que mantém o preço de compra vivo, e o preço alimenta o
   *  custo das fichas e o valor do estoque. */
  atualizarPreco: z.boolean().default(true),
  /** Só com o financeiro: a compra vira uma saída a pagar (ou já paga). */
  vencimento: dataSchema.nullish(),
  pagoEm: dataSchema.nullish(),
  formaDePagamento: z.enum(FORMAS_DE_PAGAMENTO).nullish(),
  /** Só com o financeiro: a nota recebida, registrada em Notas fiscais e ligada à saída da compra. */
  nota: notaDoLancamentoSchema.optional(),
  itens: z
    .array(
      z.object({
        materialLineId: z.string().min(1),
        quantity: quantidadeSchema,
        unitCostCents: z.number().int().min(0).max(100_000_000),
      }),
    )
    .min(1, "Adicione pelo menos um material")
    .max(100),
});
export type RegistrarCompraInput = z.infer<typeof registrarCompraSchema>;
